import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import Logo from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  paid: {
    Icon: CheckCircle2,
    iconClass: "text-emerald-400",
    title: "Payment successful",
    text: "Welcome aboard! Your plan is being activated — you now have full access to StartX Powered.",
  },
  pending: {
    Icon: Loader2,
    iconClass: "text-teal-300 animate-spin",
    title: "Confirming your payment…",
    text: "Stripe is finalizing your payment. This usually takes a few seconds.",
  },
  failed: {
    Icon: XCircle,
    iconClass: "text-red-400",
    title: "Payment didn't go through",
    text: "Your card was not charged. Please try again from the pricing section.",
  },
  cancelled: {
    Icon: XCircle,
    iconClass: "text-slate-400",
    title: "Checkout cancelled",
    text: "No charge was made. You can pick a plan whenever you're ready.",
  },
};

export default function PaymentResult({ variant }) {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [status, setStatus] = useState(
    variant === "cancel" ? "cancelled" : "pending"
  );
  const attempts = useRef(0);

  useEffect(() => {
    if (variant !== "success" || !sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/checkout/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === "paid") {
            if (!cancelled) setStatus("paid");
            return;
          }
          if (["failed", "expired"].includes(data.payment_status)) {
            if (!cancelled) setStatus("failed");
            return;
          }
        }
      } catch {}
      if (!cancelled && attempts.current++ < 10) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [variant, sessionId]);

  const copy = COPY[status] || COPY.pending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#05060F] px-6">
      <div className="grain-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center backdrop-blur-xl"
        data-testid="payment-result"
      >
        <div className="mb-8 flex justify-center">
          <Logo className="h-10 w-10" />
        </div>
        <copy.Icon className={`mx-auto mb-5 h-12 w-12 ${copy.iconClass}`} />
        <h1
          className="font-display text-2xl font-semibold tracking-tight text-white"
          data-testid="payment-result-title"
        >
          {copy.title}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">{copy.text}</p>
        <Link
          to="/"
          className="mt-8 inline-block rounded-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-500 px-7 py-3 text-sm font-semibold text-white shadow-[0_0_28px_rgba(45,212,191,0.35)] transition-[transform,box-shadow] duration-300 hover:scale-[1.03]"
          data-testid="payment-result-home-button"
        >
          Back to StartX Powered
        </Link>
      </motion.div>
    </div>
  );
}
